import {
  LAYER_COMPONENT_VIDEO,
  type CompositionLayer,
  type LayerTrim,
} from "./types";

/**
 * A layer's trim, wherever it was written. `trim` used to live in
 * `props.trim` on video layers, and saved compositions still carry it there;
 * `layer.trim` wins when both are present. Only video ever wrote the legacy
 * spot, so a custom component's own `trim` prop is left alone. Every writer
 * goes through `withLayerTrim`, so the legacy location empties out as
 * compositions are re-saved.
 */
export function getLayerTrim(layer: CompositionLayer): LayerTrim | undefined {
  const trim = layer.trim ?? getLegacyPropsTrim(layer);
  if (!trim || typeof trim !== "object") return undefined;
  const { startFrame } = trim as { startFrame?: unknown };
  return typeof startFrame === "number" && Number.isFinite(startFrame)
    ? (trim as LayerTrim)
    : undefined;
}

function getLegacyPropsTrim(layer: CompositionLayer): unknown {
  return layer.component === LAYER_COMPONENT_VIDEO
    ? (layer.props as { trim?: unknown }).trim
    : undefined;
}

/**
 * Set `layer.trim`. On video layers the legacy `props.trim` is dropped so there
 * is one copy; other components keep a `trim` prop of their own, mirroring
 * `getLayerTrim`.
 */
export function withLayerTrim(
  layer: CompositionLayer,
  trim: LayerTrim,
): CompositionLayer {
  if (layer.component !== LAYER_COMPONENT_VIDEO) return { ...layer, trim };
  const { trim: _legacy, ...props } = layer.props;
  return { ...layer, trim, props };
}
